import React, { useState } from 'react';
import { Lock, Key, Mail, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import AdminNav from './AdminNav';

interface SecretField {
  key: string;
  value: string;
  label: string;
  placeholder: string;
  description: string;
  type: 'text' | 'password';
}

export default function SecretsManager() {
  const [secrets, setSecrets] = useState<SecretField[]>([
    {
      key: 'RESEND_API_KEY',
      value: '',
      label: 'Resend API Key',
      placeholder: 're_xxxxxxxxxxxxxxxxxxxxx',
      description: 'Get your API key from https://resend.com/api-keys',
      type: 'password'
    },
    {
      key: 'FROM_EMAIL',
      value: '',
      label: 'From Email Address',
      placeholder: 'gifts@updates.birthday.coffee',
      description: 'The email address that will send birthday coffee gifts (using updates subdomain)',
      type: 'text'
    },
    {
      key: 'FROM_NAME',
      value: '',
      label: 'From Name',
      placeholder: 'Birthday Coffee',
      description: 'The name that appears in the "From" field',
      type: 'text'
    }
  ]);

  const [showSecrets, setShowSecrets] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | null; text: string }>({ type: null, text: '' });

  const handleSecretChange = (key: string, value: string) => {
    setSecrets(prev => prev.map(secret =>
      secret.key === key ? { ...secret, value } : secret
    ));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: null, text: '' });

    try {
      // Prepare secrets for submission
      const secretsData = secrets.reduce((acc, secret) => {
        if (secret.value) {
          acc[secret.key] = secret.value;
        }
        return acc;
      }, {} as { [key: string]: string });

      const response = await fetch('/api/admin/secrets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service: 'email',
          secrets: secretsData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update secrets');
      }

      setMessage({ type: 'success', text: 'Secrets updated successfully! The email worker will restart with new configuration.' });

      // Clear sensitive data from form after success
      setSecrets(prev => prev.map(secret => ({ ...secret, value: '' })));
    } catch (error) {
      setMessage({ type: 'error', text: `Failed to update secrets: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <AdminNav
        title="Secrets Manager"
        description="Manage API keys and environment variables"
      />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl p-8">

          <div className="bg-yellow-500/20 border border-yellow-400/50 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
              <div className="text-yellow-100">
                <p className="font-semibold mb-2">🔐 Security Notice</p>
                <p className="text-sm">
                  These secrets will be securely stored as encrypted environment variables in your Cloudflare Workers.
                  They are never stored in your code or database.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-500/20 border border-blue-400/50 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <Mail className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
              <div className="text-blue-100">
                <p className="font-semibold mb-2">📧 Setting up Resend</p>
                <ol className="text-sm space-y-1 list-decimal list-inside">
                  <li>Go to <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline">resend.com</a> and sign up</li>
                  <li>Verify your domain or use their testing domain</li>
                  <li>Go to API Keys and create a new key</li>
                  <li>Copy the key (starts with re_) and paste it below</li>
                </ol>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {secrets.map((secret) => (
              <div key={secret.key} className="space-y-2">
                <label className="block text-white font-semibold">
                  {secret.label}
                </label>
                <div className="relative">
                  <input
                    type={showSecrets[secret.key] ? 'text' : secret.type}
                    value={secret.value}
                    onChange={(e) => handleSecretChange(secret.key, e.target.value)}
                    placeholder={secret.placeholder}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-purple-400 pr-12"
                  />
                  {secret.type === 'password' && (
                    <button
                      type="button"
                      onClick={() => toggleShowSecret(secret.key)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                    >
                      {showSecrets[secret.key] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  )}
                </div>
                <p className="text-sm text-white/60">{secret.description}</p>
              </div>
            ))}

            {message.type && (
              <div className={`p-4 rounded-lg flex items-start gap-3 ${
                message.type === 'success' ? 'bg-green-500/20 border border-green-400/50' : 'bg-red-500/20 border border-red-400/50'
              }`}>
                {message.type === 'success' ? (
                  <Check className="w-6 h-6 text-green-400 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                )}
                <p className={message.type === 'success' ? 'text-green-100' : 'text-red-100'}>
                  {message.text}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !secrets.some(s => s.value)}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Updating Secrets...
                </>
              ) : (
                <>
                  <Key className="w-5 h-5" />
                  Update Secrets
                </>
              )}
            </button>
          </form>

          <div className="mt-8 p-4 bg-white/5 rounded-lg">
            <p className="text-white/60 text-sm">
              <strong>Note:</strong> After updating secrets, it may take a few seconds for the changes to propagate to your Workers.
              The Workers will automatically restart with the new configuration.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}